import React, { useState } from "react";
import { useBusyBlockerContext } from "../../contexts/busy-blocker-context";
import { useAppContext } from "../../hooks/useAppContext";
import { useErrorHandling } from "../../hooks/useErrorHandling";
import { useLabels } from "../../hooks/useLabels";
import { useNavigateToUrl } from "../../hooks/useNavigate";
import { FloatingMenuItem } from "./floating-menu-item";
import { labels } from "./labels";
import { useDashboardContext } from "../../contexts/dashboard-context";
import { useCart } from "../../hooks/useCart";
import { useDefinition } from "../../hooks/useDefinition";
import { useGoogleAnalytics } from "../../hooks/useGoogleAnalytics";
import { useMessage } from "../../hooks/useMessage";
import { useStableCallback } from "../../hooks/useStableCallback";

export function useBack() {
	const { editor, context, platform } = useAppContext();
	const dashboard = useDashboardContext();
	const googleAnalytics = useGoogleAnalytics();
	const definitionForRender = useDefinition();
	const navigateToUrl = useNavigateToUrl();
	const { busyBlocker } = useBusyBlockerContext();
	const { createErrorHandler } = useErrorHandling();
	const cart = useCart();

	const [clearDesignFilters, setClearDesignFilters] = useState(false);

	const hasDspUrl = !!(definitionForRender && definitionForRender.product?.designSelectionLink);

	const back = useStableCallback(() => {
		const url =
			cart && hasDspUrl && editor.definition
				? dashboard.getDesignSelectionLink(
						cart,
						editor.definition,
						platform.queryString,
						clearDesignFilters,
						context.cartItemId,
				  ) // TODO: refactor to use lazy loaded shopping cart (make async)
				: (definitionForRender &&
						dashboard.getProductUrlLink(definitionForRender.product)) ||
				  "";

		googleAnalytics.trackEventToGa({
			trackingAction: hasDspUrl ? "BackToDesigns" : "BackToProduct",
		});

		busyBlocker
			.during(editor.saveDefinitionAsActiveAsync())
			.then(() => navigateToUrl(url, { backButtonClicked: true }))
			.catch(createErrorHandler());
	});

	useMessage((message) => {
		if (
			!eXpress.core.isFirstDefinitionLoad(message) &&
			eXpress.core.isProductCodeChanged(message)
		) {
			setClearDesignFilters(true);
		}
	});

	const [[backToDesigns, backToProduct], areLabelsLoading] = useLabels([
		labels.backToDesigns,
		labels.backToProduct,
	]);

	return [
		areLabelsLoading ? null : hasDspUrl ? backToDesigns : backToProduct,
		areLabelsLoading ? null : back,
	] as const;
}

export function BackMenuItem() {
	const [backText, onBackClicked] = useBack();
	if (!backText || !onBackClicked) return null;

	return (
		<FloatingMenuItem
			text={backText || ""}
			iconName="angle-left-slim"
			onClick={onBackClicked}
		/>
	);
}
